import styled, { css } from 'styled-components';
import DateComponent from 'react-datepicker';

/* ─── Page layout ─── */

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  padding: 0 4px;
`;

export const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: #64748b;
  font-size: 12.5px;
  font-weight: 600;
  padding: 0;
  cursor: pointer;
  margin-bottom: 4px;
  font-family: inherit;

  &:hover {
    color: #006633;
  }
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;

  .titles {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    flex: 1;
  }

  h1 {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.03em;
  }

  .subtitle {
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
  }

  .actions {
    display: inline-flex;
    gap: 8px;
    flex-wrap: wrap;
  }
`;

/* ─── Grid ─── */

export const EditGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.9fr) minmax(280px, 1fr);
  gap: 16px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

export const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

export const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

/* ─── Card ─── */

export const Card = styled.div`
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.01em;
  }

  .subtitle {
    display: block;
    font-size: 11.5px;
    color: #64748b;
    margin-top: 2px;
    font-weight: 500;
  }
`;

/* ─── Title input ─── */

export const TitleInput = styled.input`
  width: 100%;
  border: none;
  outline: none;
  padding: 10px 0 14px;
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.03em;
  font-family: inherit;
  background: transparent;
  border-bottom: 2px solid #eef2f7;
  transition: border-color 0.15s ease;

  &:focus {
    border-bottom-color: #006633;
  }

  &::placeholder {
    color: #cbd5e1;
    font-weight: 700;
  }
`;

export const ExcerptInput = styled.textarea`
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13.5px;
  color: #334155;
  font-family: inherit;
  resize: vertical;
  min-height: 72px;
  background: #fafbfc;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    background 0.15s ease;
  line-height: 1.5;

  &:focus {
    border-color: #006633;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(0, 102, 51, 0.12);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

/* ─── Editor wrapper ─── */

export const EditorWrap = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;

  .tox.tox-tinymce {
    border: none !important;
    border-radius: 0 !important;
  }

  .tox .tox-edit-area__iframe {
    background: #ffffff !important;
  }

  .tox .tox-toolbar,
  .tox .tox-toolbar__primary {
    background: #fafbfc !important;
    border-bottom: 1px solid #eef2f7 !important;
  }

  .tox .tox-statusbar {
    border-top: 1px solid #eef2f7 !important;
    background: #fafbfc !important;
  }
`;

/* ─── Sidebar rows ─── */

export const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  background: #fafbfc;

  .icon {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: #e6f3ec;
    color: #006633;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .label {
    font-size: 10.5px;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .value {
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .link-action {
    background: transparent;
    border: none;
    color: #006633;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    padding: 0;
    font-family: inherit;
    text-transform: uppercase;
    letter-spacing: 0.04em;

    &:hover {
      text-decoration: underline;
    }
  }
`;

/* ─── Status chip ─── */

type StatusTone = 'warning' | 'info' | 'success';
const STATUS_MAP: Record<StatusTone, { bg: string; color: string }> = {
  warning: { bg: '#fff4e0', color: '#b45309' },
  info: { bg: '#e0ecff', color: '#1d4ed8' },
  success: { bg: '#e6f3ec', color: '#006633' },
};

export const StatusChip = styled.span<{ $tone: StatusTone }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 999px;
  ${({ $tone }) => css`
    background: ${STATUS_MAP[$tone].bg};
    color: ${STATUS_MAP[$tone].color};
  `}

  .dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
  }
`;

/* ─── Inline editors ─── */

export const InlineEditor = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 8px;

  select,
  input,
  .react-datepicker-wrapper input {
    flex: 1;
    min-width: 0;
    height: 34px;
    padding: 0 10px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 12.5px;
    font-family: inherit;
    color: #0f172a;
    background: #ffffff;
    outline: none;

    &:focus {
      border-color: #006633;
      box-shadow: 0 0 0 2px rgba(0, 102, 51, 0.1);
    }
  }

  .react-datepicker-wrapper {
    flex: 1;
    min-width: 0;
  }
`;

export const DatePicker = styled(DateComponent)`
  width: 100%;
`;

/* ─── Categories ─── */

export const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 220px;
  overflow-y: auto;
  padding-right: 4px;
`;

export const CategoryItem = styled.label<{ $checked?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 10px;
  border: 1px solid ${({ $checked }) => ($checked ? '#c7e4d5' : '#eef2f7')};
  background: ${({ $checked }) => ($checked ? '#f1faf4' : '#ffffff')};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #c7e4d5;
    background: #f7fcf9;
  }

  input {
    appearance: none;
    width: 16px;
    height: 16px;
    border: 2px solid ${({ $checked }) => ($checked ? '#006633' : '#cbd5e1')};
    border-radius: 4px;
    background: ${({ $checked }) => ($checked ? '#006633' : '#ffffff')};
    cursor: pointer;
    flex-shrink: 0;
    position: relative;
    transition: all 0.15s ease;

    &:checked::after {
      content: '';
      position: absolute;
      left: 3px;
      top: 0;
      width: 4px;
      height: 8px;
      border: solid #ffffff;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }

  .text {
    font-size: 12.5px;
    color: #334155;
    font-weight: 600;
  }
`;

/* ─── Format picker ─── */

export const FormatOptions = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
`;

export const FormatOption = styled.label<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${({ $active }) => ($active ? '#c7e4d5' : '#eef2f7')};
  background: ${({ $active }) => ($active ? '#f1faf4' : '#ffffff')};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #c7e4d5;
    background: #f7fcf9;
  }

  input {
    appearance: none;
    width: 16px;
    height: 16px;
    border: 2px solid ${({ $active }) => ($active ? '#006633' : '#cbd5e1')};
    border-radius: 50%;
    background: #ffffff;
    cursor: pointer;
    flex-shrink: 0;
    position: relative;
    transition: all 0.15s ease;

    &:checked::after {
      content: '';
      position: absolute;
      inset: 2px;
      border-radius: 50%;
      background: #006633;
    }
  }

  .label {
    font-size: 12.5px;
    color: #0f172a;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`;

/* ─── Upload areas ─── */

export const UploadArea = styled.label<{ $hasFile?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 20px 14px;
  border: 2px dashed ${({ $hasFile }) => ($hasFile ? '#c7e4d5' : '#e2e8f0')};
  border-radius: 12px;
  background: ${({ $hasFile }) => ($hasFile ? '#f7fcf9' : '#fafbfc')};
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: center;

  &:hover {
    border-color: #006633;
    background: #f7fcf9;
  }

  .icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: ${({ $hasFile }) => ($hasFile ? '#006633' : '#e6f3ec')};
    color: ${({ $hasFile }) => ($hasFile ? '#ffffff' : '#006633')};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;
  }

  .title {
    font-size: 12.5px;
    font-weight: 700;
    color: #0f172a;
  }

  .hint {
    font-size: 11px;
    color: #64748b;
    font-weight: 500;
  }

  .file-name {
    font-size: 12px;
    color: #006633;
    font-weight: 700;
    word-break: break-all;
    max-width: 100%;
  }

  input[type='file'] {
    display: none;
  }
`;

export const PreviewImage = styled.div`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #eef2f7;
  background: #f8fafc;

  img {
    display: block;
    width: 100%;
    max-height: 180px;
    object-fit: cover;
  }

  .remove {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: rgba(15, 23, 42, 0.75);
    color: #ffffff;
    border: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    backdrop-filter: blur(4px);
    transition: background 0.15s ease;

    &:hover {
      background: #b91c1c;
    }
  }
`;

/* ─── Buttons ─── */

type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
const BTN_MAP: Record<
  BtnVariant,
  {
    bg: string;
    color: string;
    border: string;
    hoverBg: string;
    hoverBorder?: string;
  }
> = {
  primary: {
    bg: '#006633',
    color: '#ffffff',
    border: '#006633',
    hoverBg: '#00451f',
  },
  secondary: {
    bg: '#ffffff',
    color: '#0f172a',
    border: '#e2e8f0',
    hoverBg: '#f8fafc',
    hoverBorder: '#006633',
  },
  ghost: {
    bg: 'transparent',
    color: '#64748b',
    border: 'transparent',
    hoverBg: '#f1f5f9',
  },
  danger: {
    bg: '#ffffff',
    color: '#b91c1c',
    border: '#fecaca',
    hoverBg: '#fef2f2',
  },
};

export const Button = styled.button<{
  $variant?: BtnVariant;
  $size?: 'sm' | 'md';
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: ${({ $size }) => ($size === 'sm' ? '30px' : '36px')};
  padding: 0 ${({ $size }) => ($size === 'sm' ? '12px' : '14px')};
  border-radius: ${({ $size }) => ($size === 'sm' ? '8px' : '10px')};
  font-size: ${({ $size }) => ($size === 'sm' ? '12px' : '13px')};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;

  ${({ $variant = 'primary' }) => css`
    background: ${BTN_MAP[$variant].bg};
    color: ${BTN_MAP[$variant].color};
    border: 1px solid ${BTN_MAP[$variant].border};

    &:hover:not(:disabled) {
      background: ${BTN_MAP[$variant].hoverBg};
      ${BTN_MAP[$variant].hoverBorder &&
      css`
        border-color: ${BTN_MAP[$variant].hoverBorder};
        color: #006633;
      `}
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
